<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from "vue";

defineOptions({ name: "LoadingAnimation" });

const props = defineProps<{
  /** When true the animation is replaced by the "ready" completion message */
  done?: boolean;
  /** Interval in milliseconds between message rotations (default 5000) */
  intervalMs?: number;
  /** Optional context message shown above rotating facts */
  contextMessage?: string;
}>();

const MESSAGES: readonly string[] = [
  // Fun Rooms To Go facts
  "🛋️ Did you know? Rooms To Go was founded in 1990 by Jeffrey and Morty Seaman in Orlando, Florida.",
  '📦 Fun fact: The founding slogan was "Buy the piece, save a little. Buy the room, save a lot!"',
  "🏆 Trivia: By 1996, Rooms To Go had become Florida's fastest-growing furniture retailer.",
  "🏪 reticulating splines.",
  "🌟 Did you know? Cindy Crawford began her design partnership with Rooms To Go in 2005.",
  "🎬 Fun fact: In 2005, Rooms To Go landed Disney's first license to produce children's bedroom suites.",
  "🏈 Trivia: Rooms To Go rolled out a licensed NFL furniture line in summer 2008.",
  "👗 Did you know? Sofia Vergara joined Rooms To Go as a brand partner in March 2013.",
  "🏪 Pivoting",
  '🎸 Fun fact: Country star Eric Church launched his "Highway to Home" collection with Rooms To Go in 2016.',
  "🪴 Trivia: In 2018, Rooms To Go acquired Carls Patio and launched the Rooms To Go Patio division.",
  "👶 Did you know? Rooms To Go Kids opened its first location in Marietta, Georgia, in 1997 — pioneering dedicated children's furniture retail.",
  "💰 Fun fact: Rooms To Go reached $3.8 billion in revenue in 2022, making it one of the top furniture retailers in the US.",
  "🏪 Trivia: As of 2024 Rooms To Go operates 159 locations across the United States.",
  "🧠 Almost there — cross-referencing purchase orders and inventory data…",
  "⚡ Hang tight — the algorithm is crunching SKU-level redistribution scenarios…",
  "🔍 Still working — validating cutting-date results against related purchase orders…",
];

let DONE_MESSAGE = "your results are ready!";

const currentIndex = ref(0);
const visible = ref(true);

let rotationTimer: ReturnType<typeof setTimeout> | undefined;
let fadeTimer: ReturnType<typeof setTimeout> | undefined;

function clearTimers() {
  if (rotationTimer) {
    clearTimeout(rotationTimer);
    rotationTimer = undefined;
  }

  if (fadeTimer) {
    clearTimeout(fadeTimer);
    fadeTimer = undefined;
  }
}

function pickNextIndex() {
  if (MESSAGES.length <= 1) {
    return 0;
  }

  let nextIndex = currentIndex.value;
  while (nextIndex === currentIndex.value) {
    nextIndex = Math.floor(Math.random() * MESSAGES.length);
  }

  return nextIndex;
}

function scheduleNext() {
  clearTimers();

  if (props.done) {
    visible.value = true;
    return;
  }

  rotationTimer = setTimeout(() => {
    visible.value = false;
    fadeTimer = setTimeout(() => {
      currentIndex.value = pickNextIndex();
      visible.value = true;
      scheduleNext();
    }, 400);
  }, props.intervalMs ?? 5000);
}

watch(
  () => props.done,
  (done) => {
    if (done) {
      clearTimers();
      visible.value = true;
      return;
    }

    currentIndex.value = 0;
    visible.value = true;
    scheduleNext();
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  clearTimers();
});

const displayMessage = computed(() =>
  props.done ? DONE_MESSAGE : MESSAGES[currentIndex.value]
);
</script>

<template>
  <div class="loading-overlay" :class="{ 'loading-overlay--done': done }">
    <p v-if="contextMessage" class="loading-overlay-context">
      {{ contextMessage }}
    </p>
    <span v-if="!done" class="loading" aria-hidden="true">
      <span></span>
      <span></span>
      <span></span>
      <span></span>
    </span>
    <p
      class="loading-overlay-message"
      :class="{ 'loading-overlay-message-visible': visible || done }"
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      {{ displayMessage }}
    </p>
  </div>
</template>

<style scoped>
.loading-overlay {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.25rem;
  padding: 2rem;
}

.loading-overlay-message {
  max-width: 36rem;
  text-align: center;
  font-size: 0.9rem;
  color: #374151;
  opacity: 0;
  transition: opacity 0.4s ease;
}

.loading-overlay-context {
  max-width: 40rem;
  margin: 0;
  text-align: center;
  font-size: 0.95rem;
  font-weight: 600;
  color: #1f2937;
}

.loading-overlay-message-visible {
  opacity: 1;
}
</style>
